/**
 * Device activity helpers (migration 023). "Activity" is the most-recent
 * interactive logon mirrored from Intune `usersLoggedOn` — distinct from the
 * Intune *sync* time, which only tells you the agent checked in.
 */
import type { Asset } from '@/types/oscar';

/** A device is "active" if it had a logon within this many days. Mirrors the API ActiveWindowDays. */
export const ACTIVE_WINDOW_DAYS = 30;

/** Days since the last logon, or null when there's no sign-in data. */
export function daysSinceLogon(value: string | null | undefined): number | null {
  if (!value) return null;
  // API DATETIME2 comes back UTC without a designator — read it as UTC.
  const d = new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(value) ? value : `${value}Z`);
  if (Number.isNaN(d.getTime())) return null;
  return (Date.now() - d.getTime()) / 86_400_000;
}

/** True when the device logged on within the active window. */
export function isActiveDevice(lastLogonDate: string | null | undefined): boolean {
  const days = daysSinceLogon(lastLogonDate);
  return days != null && days <= ACTIVE_WINDOW_DAYS;
}

/**
 * Does the Intune primary user match the PERSON Oscar has the device assigned to?
 *
 * Marty, 29/10/2026: devices showed no person in Oscar and were missing from TeamViewer because the primary
 * user in Intune was never updated after the device was assigned. Mirrors `PrimaryUserVsAssigneeSql`.
 *
 * - `unknown`: nothing to compare - not enrolled in Intune, or not assigned to a person.
 * - `matches`: the primary user is the assigned person.
 * - `none`: assigned to a person, but Intune has NO primary user. The case that was found.
 * - `differs`: assigned to a person, but Intune names someone else.
 */
export type PrimaryUserVsAssignee = 'unknown' | 'matches' | 'none' | 'differs';

export function primaryUserVsAssignee(
  a: Pick<Asset, 'IntuneDeviceId' | 'IntuneAssignedUpn' | 'AssignedPersonEmail'>,
): PrimaryUserVsAssignee {
  const assignee = a.AssignedPersonEmail?.trim().toLowerCase();
  if (!a.IntuneDeviceId || !assignee) return 'unknown';

  const primary = a.IntuneAssignedUpn?.trim().toLowerCase();
  if (!primary) return 'none';
  return primary === assignee ? 'matches' : 'differs';
}

/**
 * Laptops/desktops whose last interactive user differs from the Intune primary
 * user — the "primary user incorrect" alert. Mirrors PrimaryUserMismatchSql.
 */
export function isPrimaryUserMismatch(
  a: Pick<Asset, 'Category' | 'IntuneAssignedUpn' | 'IntuneLastLogonUpn'>,
): boolean {
  if (a.Category !== 'Laptop' && a.Category !== 'Desktop') return false;
  const primary = a.IntuneAssignedUpn?.trim().toLowerCase();
  const last = a.IntuneLastLogonUpn?.trim().toLowerCase();
  return !!primary && !!last && primary !== last;
}
